import { Linkify } from "@/components/Linkify";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { HeartPulse, Printer, CalendarClock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

export default function Aftercare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/aftercare/${token}`).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-muted-foreground p-6" data-testid="aftercare-error">These aftercare instructions couldn't be found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const logo = data.business?.logo_url ? (data.business.logo_url.startsWith("/") ? API + data.business.logo_url : data.business.logo_url) : null;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="aftercare-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            {logo ? <img src={logo} alt={data.business?.name} className="h-12 w-12 rounded-xl object-cover border border-border" /> : <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center"><HeartPulse className="h-6 w-6 text-primary" /></div>}
            <div>
              <div className="font-heading font-extrabold text-lg">{data.business?.name || "Your clinic"}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest font-mono-label">Aftercare instructions</div>
            </div>
          </div>
          <button onClick={() => window.print()} className="no-print inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-border text-sm font-medium hover:bg-muted" data-testid="aftercare-print"><Printer className="h-4 w-4" /> Print</button>
        </div>

        {data.next_appointment && (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 mb-5 flex items-start gap-3" data-testid="aftercare-next-appt">
            <CalendarClock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono-label text-primary">Your next appointment</div>
              <div className="font-semibold mt-0.5">{fmtDT(data.next_appointment.start)}</div>
              <div className="text-sm text-muted-foreground">{data.next_appointment.service}{data.next_appointment.staff ? ` · with ${data.next_appointment.staff}` : ""}</div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight" data-testid="aftercare-title">{data.title}</h1>
          {data.client_name && <p className="text-muted-foreground text-sm mt-1">Prepared for {data.client_name}</p>}
          <div className="mt-6 text-base leading-relaxed whitespace-pre-wrap" data-testid="aftercare-instructions"><Linkify text={data.instructions} /></div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">If you have any concerns, please contact {data.business?.name || "your clinic"}{data.business?.phone ? ` on ${data.business.phone}` : ""}.</p>
      </div>
    </div>
  );
}
